import { useTranslations } from 'use-intl';

import {
  CompanyDraftContent,
  DraftAgent,
  DraftRepresentative,
  DraftShareholder,
} from '@blockpulse3/graphql/hooks';
import { Step, StepState, Wizard } from '@blockpulse3/ui/commons';
import { useStep } from '@blockpulse3/ui/ui-hooks';

import {
  FinancialInformationsStep,
  LegalInformationsStep,
  RepresentantInformationsStep,
} from './Steps';
import { OtherRoleStep } from './Steps/OtherRoleStep';
import { ShareholderInformationsStep } from './Steps/ShareholderInformationsStep';
import { SummaryStep } from './Steps/SummaryStep';
import { useSPVForm } from './provider';

type Props = {
  onChange: (data: CompanyDraftContent, onSuccess?: () => void) => void;
  onCreate: (data: CompanyDraftContent) => void;
  onSubmit: () => void;
};

/**
 * SPVFormWizard.
 * Form wizard that handles the creation of a SPV.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SPVFormWizard({ onChange, onCreate, onSubmit }: Props): JSX.Element {
  const i18nSPVFormWizard = useTranslations('SPVFormWizard');

  const { setCompanyDraftContent, companyDraftContent: spvFormData } = useSPVForm();
  const [currentStep, { goToPrevStep, goToNextStep, setStep }] = useStep({ maxStep: 5 });

  const handleLegalInfosSubmit = (data: CompanyDraftContent): void => {
    setCompanyDraftContent({ ...spvFormData, ...data });
    if (currentStep === 0 && window.location.pathname.endsWith('create')) {
      onCreate({ ...spvFormData, ...data });
    } else {
      onChange({ ...spvFormData, ...data });
    }
    goToNextStep();
  };

  const handleFinancialInfosSubmit = (data: CompanyDraftContent): void => {
    setCompanyDraftContent({ ...spvFormData, ...data });
    onChange({ ...spvFormData, ...data });
    goToNextStep();
  };

  const handleRepresentantInfosSubmit = (data: DraftRepresentative[]): void => {
    const initialShareholder = spvFormData.initialShareholder;
    const { nominalValue, revocationMajorityData, role, ...president } = data[0];

    /* ** TODO: company draft content refactor needed ** */
    const shareholder = {
      ...initialShareholder,
      ...president,
      type: president.type,
      representative: {
        ...initialShareholder.representative,
        firstName: president.firstName,
        lastName: president.lastName,
        email: president.email,
        position: role,
      },
      preferredSharesPercentages: initialShareholder.preferredSharesPercentages,
    };

    setCompanyDraftContent({
      ...spvFormData,
      representatives: [...data],
      initialShareholder: shareholder,
    });

    onChange(
      {
        ...spvFormData,
        representatives: [...data],
        initialShareholder: shareholder,
      },
      goToNextStep,
    );
  };

  const handleShareholderSubmit = (data: DraftShareholder): void => {
    setCompanyDraftContent({ ...spvFormData, initialShareholder: { ...data } });
    onChange({ ...spvFormData, initialShareholder: { ...data } });
    goToNextStep();
  };

  const handleOtherRoleSubmit = (data: DraftAgent[]): void => {
    setCompanyDraftContent({ ...spvFormData, agents: [...data] });
    onChange({ ...spvFormData, agents: [...data] }, goToNextStep);
  };

  const handleWizardSubmit = (): void => {
    onSubmit();
  };

  const handlePreviousStep = (): void => {
    goToPrevStep();
  };

  /* ** Handle previous step return ** */
  const handleStepClick = (index: number, { isCompleted }: StepState): void => {
    if (isCompleted) {
      setStep(index);
    }
  };

  return (
    <Wizard currentStep={currentStep} onStepClick={handleStepClick}>
      <Step title={i18nSPVFormWizard('LegalInformationsStep')}>
        <LegalInformationsStep onSubmit={handleLegalInfosSubmit} />
      </Step>
      <Step title={i18nSPVFormWizard('FinancialInformationsStep')}>
        <FinancialInformationsStep
          onCancel={handlePreviousStep}
          onSubmit={handleFinancialInfosSubmit}
        />
      </Step>
      <Step title={i18nSPVFormWizard('RepresentantInformationsStep')}>
        <RepresentantInformationsStep
          onCancel={handlePreviousStep}
          onSubmit={handleRepresentantInfosSubmit}
        />
      </Step>
      <Step title={i18nSPVFormWizard('ShareholderInformationsStep')}>
        <ShareholderInformationsStep
          onCancel={handlePreviousStep}
          onSubmit={handleShareholderSubmit}
        />
      </Step>
      <Step title={i18nSPVFormWizard('OtherRoleStep')}>
        <OtherRoleStep onCancel={handlePreviousStep} onSubmit={handleOtherRoleSubmit} />
      </Step>
      <Step title={i18nSPVFormWizard('SummaryStep')}>
        <SummaryStep onCancel={handlePreviousStep} onSubmit={handleWizardSubmit} />
      </Step>
    </Wizard>
  );
}

export type SPVFormWizardProps = Props;
